import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import styled from 'styled-components';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
import getThreadIdFromURL from '../../selectors/getThreadIdFromURL';
import { getCurrentInboxId, isMissingInboxPermission, getInboxRequestStatus } from 'conversations-inbox-lib/unified-inbox/public/selectors';
import { useViews } from 'find-and-filter-data/views-data/public';
import { isViewActive } from 'conversations-inbox-lib/views/public/operators';
import AsyncInboxZeroState from '../../code-splitting/components/AsyncInboxZeroState';
import { fetchInstallStatus } from '../../verify-install/actions/fetchInstallStatus';
import { getFocusedViewMemberThreadId } from '../../focused-view-member/selectors/getFocusedViewMemberThreadId';
import MissingPermission from './MissingPermission';
import SLAConfigOnboardingModal from '../../sla-onboarding/components/SLAConfigOnboardingModal';
import { getShouldEvenConsiderShowingSLAModal } from '../../sla-onboarding/selectors/slaSelectors';
import InboxViewSelector from './InboxViewSelector';
import InboxWarningBanner from './InboxWarningBanner';
import InboxVerifyFBMPrompt from './InboxVerifyFBMPrompt';
import InboxVerifyScriptPrompt from './InboxVerifyScriptPrompt';
import { InboxUnauthenticatedDomainsPrompt } from './InboxUnauthenticatedDomainsPrompt';
import ErrorBoundary from 'conversations-error-reporting/error-boundaries/components/ErrorBoundary';
import BatchThreadOperationsToolbarWrapper from '../../batch-thread-operations-toolbar/containers/BatchThreadOperationsToolbarWrapper';
import InboxPreviewCardList from './InboxPreviewCardList';
import InboxMainViewContent from './InboxMainViewContent';
import { isInSearchAndFilterMode as getIsInSearchAndFilterMode } from 'conversations-inbox-lib/thread-search-and-filtering/public/selectors';
import SearchBar from '../../thread-search-and-filtering/components/SearchBar';
import SearchAndFilteringMainViewContent from '../../thread-search-and-filtering/components/SearchAndFilteringMainViewContent';
import ConnectedBanners from '../../banner/containers/ConnectedBanners';
import { FAILED } from 'conversations-async-data/async-data/constants/asyncStatuses';
import FatalError from 'conversations-error-reporting/error-boundaries/components/FatalError';
import FatalTopLevelError from '../../error-messages/components/FatalTopLevelError';
import { useSearchValue } from 'find-and-filter-data/search-and-filter-data/public';
import { ConversationHandoffErrorContextProvider } from 'conversations-thread-view/move-to-inbox/context/ConversationHandoffErrorContext';
import { SEARCH_BAR_SECTION_HEIGHT } from '../constants/InboxContainerConstants';
import { useOmnibusPolling } from '../../omnibus/hooks/useOmnibusPolling';
import InboxCallingWidgetContainer from '../../calling/components/InboxCallingWidgetContainer';
import { TICKET } from 'customer-data-objects/constants/ObjectTypes';
import { usePrefetchAccessLevels } from 'customer-data-properties/accessLevel/AccessLevelContext';
import { useInboxChannels } from '../../zero-state/hooks/useInboxChannels';
import { getZeroState } from '../../zero-state/selectors/getZeroState';
import { isEmailSetup } from '../../zero-state/operators/getters';
import { getIsUngatedForHelpDesk, getIsUngatedToUseCallTab } from '../../selectors/gateSelectors';
import InboxMigrationBanner from './migration-banner/InboxMigrationBanner';
import { useHydrateThreadListMembers } from '../../thread-list-member-sidebar/hooks/useHydrateThreadListMembers';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledUIFlex = styled(UIFlex).withConfig({
  displayName: "InboxContainer__StyledUIFlex",
  componentId: "sc-1w11uhd-0"
})(["height:100%;width:100%;align-items:stretch;"]);
const MidSidebar = styled(UIBox).withConfig({
  displayName: "InboxContainer__MidSidebar",
  componentId: "sc-1w11uhd-1"
})(["min-width:260px;max-width:376px;width:21vw;@media (min-width:1920px){max-width:420px;}"]);
export const SearchBarSection = styled(UIBox).withConfig({
  displayName: "InboxContainer__SearchBarSection",
  componentId: "sc-1w11uhd-2"
})(["height:", "px;border-bottom:1px solid ", ";"], SEARCH_BAR_SECTION_HEIGHT, GREAT_WHITE);

// this is a hack - since the left sidebar flex-basis is constantly changing
// we do this so flex-grow calculates all the empty space as "remaining space"
// and then distributes it back to this UIBox
export const InboxViewContent = styled(UIBox).withConfig({
  displayName: "InboxContainer__InboxViewContent",
  componentId: "sc-1w11uhd-3"
})(["height:100%;width:0;"]);
const shouldShowZeroState = (channels, inboxId, zeroState) => {
  if (channels) {
    const onlyFallbackEmail = !channels.hasMore && !isEmailSetup(inboxId, zeroState);
    const onlyEmailChannel = !channels.hasMore && isEmailSetup(inboxId, zeroState);
    const hasOtherConnectedChannels = channels.hasMore && !isEmailSetup(inboxId, zeroState);
    const hasOtherChannelsAndEmail = channels.hasMore && isEmailSetup(inboxId, zeroState);
    if (onlyFallbackEmail) {
      return true;
    } else if (onlyEmailChannel || hasOtherConnectedChannels || hasOtherChannelsAndEmail) {
      return false;
    }
  }
  return true;
};
const InboxContainer = ({
  match
}) => {
  const focusedThreadId = useSelector(getFocusedViewMemberThreadId);
  const currentInboxId = useSelector(getCurrentInboxId);
  const channels = useInboxChannels(currentInboxId).asyncChannels.data;
  const isUngatedForHelpDesk = useSelector(getIsUngatedForHelpDesk);
  const showMissingPermission = useSelector(isMissingInboxPermission);
  const shouldEvenConsiderShowingSLAModal = useSelector(getShouldEvenConsiderShowingSLAModal);
  const {
    loading,
    viewMembers
  } = useHydrateThreadListMembers();
  const zeroState = useSelector(getZeroState);
  const {
    views
  } = useViews(currentInboxId);
  const hasActiveView = views.some(view => isViewActive(view));
  const showZeroState = hasActiveView || viewMembers.size > 0 || loading ? false : shouldShowZeroState(channels, currentInboxId, zeroState);
  const hasVeryActiveView = views.some(view => isViewActive(view, 10));
  const showSLAModal = shouldEvenConsiderShowingSLAModal && hasVeryActiveView;
  const initialThreadId = useSelector(state => getThreadIdFromURL(state, {
    match
  }));
  useOmnibusPolling(initialThreadId);
  usePrefetchAccessLevels({
    objectTypes: [TICKET]
  });
  const isInSearchAndFilterMode = useSelector(getIsInSearchAndFilterMode);
  const {
    searchValue,
    setSearchValue,
    clearSearchValue
  } = useSearchValue();
  const inboxRequestStatus = useSelector(getInboxRequestStatus);
  const isUngatedToUseCallTab = useSelector(getIsUngatedToUseCallTab);
  const dispatch = useDispatch();
  const [mounted, setMounted] = useState(false);
  if (!mounted) {
    dispatch(fetchInstallStatus({}));
  }
  useEffect(() => {
    setMounted(true);
  }, []);
  if (inboxRequestStatus === FAILED) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "INBOX_REQUEST_STATUS_FAILED",
      children: /*#__PURE__*/_jsx(FatalError, {})
    });
  }
  if (showMissingPermission) {
    // @ts-expect-error ts-migrate(2322) FIXME: Type '{}' is not assignable to type 'never'.
    return /*#__PURE__*/_jsx(MissingPermission, {});
  }
  const renderContent = () => {
    if (showZeroState && !isInSearchAndFilterMode) {
      return /*#__PURE__*/_jsx(AsyncInboxZeroState, {});
    }
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "INBOX_SUCCESS",
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "stretch",
        className: "height-100 width-100",
        style: {
          position: 'relative'
        },
        role: "main",
        children: [/*#__PURE__*/_jsx(BatchThreadOperationsToolbarWrapper, {}), /*#__PURE__*/_jsx(MidSidebar, {
          className: "flex-column",
          grow: 0,
          shrink: 0,
          children: /*#__PURE__*/_jsx(InboxPreviewCardList, {
            focusedThreadId: focusedThreadId
          })
        }), !isInSearchAndFilterMode ? /*#__PURE__*/_jsx(InboxMainViewContent, {
          focusedThreadId: focusedThreadId
        }) : /*#__PURE__*/_jsx(SearchAndFilteringMainViewContent, {
          focusedThreadId: focusedThreadId
        })]
      })
    });
  };
  return /*#__PURE__*/_jsxs(ErrorBoundary, {
    errorLoggingTitle: "InboxContainer",
    renderError: FatalTopLevelError,
    children: [!isUngatedToUseCallTab && /*#__PURE__*/_jsx(InboxCallingWidgetContainer, {}), /*#__PURE__*/_jsx(ConversationHandoffErrorContextProvider, {
      children: /*#__PURE__*/_jsxs(StyledUIFlex, {
        direction: "column",
        children: [/*#__PURE__*/_jsx(InboxWarningBanner, {}), /*#__PURE__*/_jsx(InboxVerifyScriptPrompt, {}), /*#__PURE__*/_jsx(InboxVerifyFBMPrompt, {}), /*#__PURE__*/_jsx(InboxUnauthenticatedDomainsPrompt, {}), showSLAModal && /*#__PURE__*/_jsx(SLAConfigOnboardingModal, {}), isUngatedForHelpDesk && /*#__PURE__*/_jsx(InboxMigrationBanner, {
          inboxId: currentInboxId
        }), /*#__PURE__*/_jsxs(StyledUIFlex, {
          children: [/*#__PURE__*/_jsx(InboxViewSelector, {}), /*#__PURE__*/_jsx(InboxViewContent, {
            grow: 1,
            shrink: 1,
            children: /*#__PURE__*/_jsxs(StyledUIFlex, {
              direction: "column",
              children: [/*#__PURE__*/_jsx(UIBox, {
                className: "width-100",
                children: /*#__PURE__*/_jsx(ConnectedBanners, {})
              }), isInSearchAndFilterMode && /*#__PURE__*/_jsx(SearchBarSection, {
                className: "p-x-3 p-y-2 width-100",
                children: /*#__PURE__*/_jsx(SearchBar, {
                  clearSearchValue: clearSearchValue,
                  searchValue: searchValue,
                  setSearchValue: setSearchValue
                })
              }), renderContent()]
            })
          })]
        })]
      })
    })]
  });
};
InboxContainer.displayName = 'InboxContainer';
export { InboxContainer as UnwrappedContainer };
export default InboxContainer;